import { RecapturedRevenue, RecapturedRevenueAssumptions } from '../../../../../shared/api/controllers/engagementAnalyticsReporting';

/**
 * Starting assumptions for complex mode. Staff, hold time and the unanswered rate match the server defaults in RecapturedRevenueAssumptions;
 * the never-order rate starts at 50% instead of the server's 20%, applied in the browser by applyRecapturedRevenueRates.
 */
export const DEFAULT_RECAPTURED_REVENUE_ASSUMPTIONS: RecapturedRevenueAssumptions = { staffCount: 1, holdLimitSeconds: 60, missedWhenFreeRate: 0.2, lostCustomerRate: 0.5 };

/**
 * Staff count and hold time decide which orders count as busy-line, so only the server can apply them.
 * No edits means the defaults, so a rate-only edit keeps the same key and doesn't refetch.
 */
export const getRecapturedRevenueServerKey = (assumptions: RecapturedRevenueAssumptions | null) => {
    const { staffCount, holdLimitSeconds } = assumptions || DEFAULT_RECAPTURED_REVENUE_ASSUMPTIONS;
    return `${staffCount}|${holdLimitSeconds}`;
};

/**
 * What to send the server: the edited staff and hold time with the default rates. The rates are applied in the browser
 * (applyRecapturedRevenueRates), and nonzero rates from the server keep a 0% slider from blocking later scaling.
 */
export const getRecapturedRevenueServerAssumptions = (assumptions: RecapturedRevenueAssumptions | null): RecapturedRevenueAssumptions | undefined =>
    assumptions
        ? { ...assumptions, missedWhenFreeRate: DEFAULT_RECAPTURED_REVENUE_ASSUMPTIONS.missedWhenFreeRate, lostCustomerRate: DEFAULT_RECAPTURED_REVENUE_ASSUMPTIONS.lostCustomerRate }
        : undefined;

/**
 * Applies edited unanswered and never-order rates to an estimate in the browser, so sliders update instantly.
 * Busy-line revenue scales with the never-order rate and no-answer revenue with unanswered × never-order,
 * matching the server calculation. A rate the server used as 0 leaves nothing to scale, so the model is kept as is.
 */
export const applyRecapturedRevenueRates = (model: RecapturedRevenue, assumptions: RecapturedRevenueAssumptions | null): RecapturedRevenue => {
    const base = model?.assumptions;
    if (!assumptions || !base) return model;
    const baseBusyRate = base.lostCustomerRate;
    const baseFreeRate = base.missedWhenFreeRate * base.lostCustomerRate;
    if (!(baseBusyRate > 0) || !(baseFreeRate > 0)) return model;

    const busyFactor = assumptions.lostCustomerRate / baseBusyRate;
    const freeFactor = (assumptions.missedWhenFreeRate * assumptions.lostCustomerRate) / baseFreeRate;
    const busyLineRecapturedRevenue = model.busyLineRecapturedRevenue * busyFactor;
    const freeLineRecapturedRevenue = model.freeLineRecapturedRevenue * freeFactor;
    return {
        ...model,
        busyLineRecapturedRevenue,
        freeLineRecapturedRevenue,
        recapturedRevenue: busyLineRecapturedRevenue + freeLineRecapturedRevenue,
        assumptions: { ...base, missedWhenFreeRate: assumptions.missedWhenFreeRate, lostCustomerRate: assumptions.lostCustomerRate },
        histogram: (model.histogram || []).map(bucket => {
            const busy = bucket.busyLineRecapturedRevenue * busyFactor;
            const free = bucket.freeLineRecapturedRevenue * freeFactor;
            return { ...bucket, busyLineRecapturedRevenue: busy, freeLineRecapturedRevenue: free, recapturedRevenue: busy + free };
        })
    };
};
